import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ValidationType } from "src/enums";
import { Field } from "src/models";
import { StringService, ValidationService, _ } from "src/services";
import { ContactUsAction, ContactUsState, UserSession } from "src/store";
import ReCAPTCHA from "react-google-recaptcha";
import { Config } from "src/constants";
import { Call, WhatsApp } from "src/components";


export const ContactUs: React.FC = () => {


    //#region dependencies

    const dispatch = useDispatch();
    const user: UserSession = useSelector((state: any) => state.UserState);
    const contactUs: ContactUsState = useSelector((state: any) => state.ContactState);

    //#end region

    //#region properties

    const recaptchaRef = useRef<ReCAPTCHA>(null);

    //#end region

    //#region state

    const [state, setState] = useState<(Field)[]>([
        {
            order: 0,
            name: "fullName",
            title: _("fullName"),
            placeholder: _("fullName"),
            value: "",
            error: "",
            unauth: true,
            validations: [ValidationType.Required]
        },
        {
            order: 1,
            name: "mobile",
            title: _("cell"),
            placeholder: _("cell"),
            value: "",
            error: "",
            unauth: true,
            validations: [ValidationType.Required, ValidationType.Cell]
        },
        {
            order: 3,
            name: "feedbackType",
            title: _("feedback_feedbackType"),
            placeholder: _("feedback_chooseFeedbackType"),
            value: "",
            error: "",
            number: true,
            options: contactUs.types,
            validations: [ValidationType.Required]
        },
        {
            order: 4,
            name: "body",
            title: _("messageBody"),
            placeholder: _("messageBodyPlaceholder"),
            value: "",
            error: "",
            multiline: true,
            validations: [ValidationType.Required]
        },
        {
            order: 5,
            name: "captcha",
            title: _("captch"),
            placeholder: "",
            value: "",
            error: "",
            unauth: true,
            recaptcha: true,
            validations: [ValidationType.Required]
        },
    ]);

    //#end region

    //#region methods

    const validate = (name: string | null, value: string) => {
        const model = ValidationService.validateModelByName(state.filter((x) => !x.unauth || !user.loggedIn), name, value);
        const fromState = state.filter((x) => x.unauth && user.loggedIn);

        setState([...model.fields, ...fromState]);
        return model.valid;
    };

    const onChange = (name: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const value = e.target.value;
        validate(name, value);
    };

    const onReCaptchaChange = (name: string, value: string | null) => {
        validate(name, value || "");
    };

    const onSubmit = () => {
        const valid = validate(null, "");
        if (valid) {
            const model: Record<string, any> = {};

            state.filter((x) => !x.unauth || !user.loggedIn).forEach((x) => {
                model[x.name] = StringService.normalize(x.value);
                if (x.number) model[x.name] = +model[x.name];
            });

            dispatch(ContactUsAction.submit(model));
        }
    };

    //#end region

    //#region effects

    useEffect(() => {

        const onReset = () => {
            const s = [...state];
            s.forEach((x) => {
                x.value = "";
                x.error = "";
            });
            setState([...s]);

            if (recaptchaRef && recaptchaRef.current)
                recaptchaRef.current.reset();
        };


        if (!contactUs.loading && contactUs.succeed) {
            onReset();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactUs]);

    useEffect(() => {
        if (!contactUs.typesLoaded) dispatch(ContactUsAction.getFeedbackTypes());
    }, [dispatch, contactUs.typesLoaded]);

    useEffect(() => {

        setState((prev) => prev.map((x) => ({
            ...x,
            options: x.name === "feedbackType" ? contactUs.types : x.options
        })));

    }, [contactUs.types]);

    //#end region

    return (
        <div className="page-about-us  app-theme2">
            <div className="cover d-flex">
                <div className="container d-flex">
                    <h1>
                        <span>{_("contactUs")}</span>
                    </h1>
                    <h2 className="text-center">{_("contactUsDescription")}</h2>
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between contacts-links">
                        {
                            _("supportTell") &&
                            <a href={`tel:${_("supportTell")}`} className="d-flex align-items-center">
                                <Call />
                                <span>{_("supportTell")}</span>
                            </a>
                        }
                        {
                            _("antenWhatsAppLink") &&
                            <a href={_("antenWhatsAppLink")} className="d-flex align-items-center">
                                <WhatsApp />
                                <span>{_("antenWhatsApp")}</span>
                            </a>
                        }
                    </div>
                </div>
            </div>
            <div className="contact-us container">
                {
                    state.filter((field) => !field.unauth || !user.loggedIn)
                        .sort((x, y) => x.order - y.order)
                        .map((field, index) => (
                            <div className="form-group" key={field.name}>
                                {!field.recaptcha && <label>{field.title}</label>}
                                {
                                    field.recaptcha &&
                                    <div className={`${field.error ? " is-invalid" : ""}`}>
                                        <ReCAPTCHA
                                            hl="fa"
                                            ref={recaptchaRef}
                                            size="normal"
                                            sitekey={Config.gRecaptchaSitekey}
                                            theme="dark"
                                            onChange={(value) => onReCaptchaChange(field.name, value)}
                                        />
                                    </div>
                                }
                                {
                                    !field.recaptcha && field.options &&
                                    <select
                                        className={`text-right mb-3 w-100 form-control dropdown${field.error ? " is-invalid" : ""}`}
                                        value={field.value}
                                        onChange={(e) => onChange(field.name, e)}>
                                        <option title={field.placeholder} value={undefined}>{field.placeholder}</option>
                                        {field.options.map((x) => (
                                            <option
                                                title={x.text}
                                                value={x.value}
                                                key={x.value}>{x.text}</option>)
                                        )}
                                    </select>
                                }
                                {
                                    !field.recaptcha && !field.options && field.multiline &&
                                    <textarea
                                        placeholder={field.placeholder}
                                        className={`form-control focusable${field.error ? " is-invalid" : ""}`}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => onChange(field.name, e)}
                                    />
                                }
                                {
                                    !field.recaptcha && !field.options && !field.multiline &&
                                    <input
                                        placeholder={field.placeholder}
                                        className={`form-control focusable${field.error ? " is-invalid" : ""}`}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => onChange(field.name, e)}
                                    />
                                }
                                {field.error && <div className="invalid-feedback text-rtl">{field.error}</div>}
                            </div>
                        ))
                }
                <div className="form-group float-left col-xs-12 col-sm-5 contact-us--submit d-flex align-items-center justify-content-end mb-xs-2 px-0">
                    <button className={`btn btn-md btn-primary focusable pull-left text-rtl w-100${contactUs.loading ? " btn-loading" : ""}`} onClick={onSubmit}>{_("send")}</button>
                </div>
            </div>
        </div>
    );
};